import { useEffect, useState } from 'react';
import type { UsageSnapshot } from '@reflex/shared';
import { getSocketManager } from '@/lib/socketManager';
import { useSocketConnection, useSocketMessage } from '@/hooks/socket/useSocket';

/**
 * Subscribe to the admin usage snapshot pushed over the existing
 * WebSocket connection. The server only honours the subscription when
 * the caller has `admin:read` (or `isSuperAdmin`); pages that mount this
 * should themselves be gated by `RequireSuperAdmin` so we don't trigger
 * a server-side error for non-admins.
 *
 * Returns the most recent snapshot (or `null` until the first one
 * arrives) plus the live socket connection state, so callers can
 * distinguish "still connecting" from "subscribed but no data yet".
 */
export function useUsageSnapshot(enabled: boolean = true): {
  snapshot: UsageSnapshot | null;
  connectionState: ReturnType<typeof useSocketConnection>;
} {
  const [snapshot, setSnapshot] = useState<UsageSnapshot | null>(null);
  const connectionState = useSocketConnection();

  useEffect(() => {
    if (!enabled) return;
    const manager = getSocketManager();
    manager.subscribeToUsage();
    return () => {
      manager.unsubscribeFromUsage();
    };
  }, [enabled]);

  useSocketMessage((msg) => {
    if (!enabled) return;
    if (msg.type !== 'usage:snapshot') return;
    setSnapshot(msg.snapshot);
  });

  return { snapshot, connectionState };
}
